/**
 * Hook de la pestaña "Resumen" de /finanzas-empresa.
 *
 * Consolida 5 endpoints de finanzas empresa en totales agregados para
 * calcular ingresos, egresos y utilidad neta de Aloja Fácil Empresa.
 *
 * Fuente de verdad de las fórmulas: docs/flujo-financiero.md §4 (snapshot
 * por arriendo) y §7.2 (utilidad empresa mensual).
 *
 * Regla clave (§4): el campo `ingreso_af_clp` de cada booking ya consolida
 * comisiones + IVA + transporte + materiales + lavandería (esta última
 * solo cuando la propiedad apunta a la "Lavandería General Externa") −
 * descuentos solo AF. Por eso NO se suman aparte los registros de
 * `lavados` — están en otro panel (`/lavanderia`) con su propia
 * contabilidad por sub-entidad. Ver bloque iter 26 más abajo.
 *
 * Round 2026-05-11 iter 26 — modelo unificado de lavandería:
 * - Toda lavandería del catálogo (genérica externa o AF interna) es una
 *   sub-entidad con contabilidad propia → `/lavanderia`.
 * - AF Empresa cobra el subitem lavandería SIEMPRE (vía limpieza_clp en
 *   ingreso_af_clp y/o limpieza_clp del booking). Para AF interna se
 *   cancela vía intermediario en el cron `utilidad_empresa`. Para genérica
 *   externa se resta el costo real anotado a mano (lavados rol='paga' +
 *   gastos op).
 * - Por eso este hook NO carga lavados: serían doble conteo o ruido.
 *
 * Egresos considerados acá:
 * - Sueldos (expenses categoria='sueldo'). Bonos extra al aseador van
 *   acá vía `concepto='bono_extra'` (no en aseos).
 * - Gastos operacionales (expenses sin categoria='sueldo')
 * - Sueldos de aseadores (cleaning.sueldoAseador) vía helper de flujos.
 * - Compras de productos (inventory_transaction tipo='compra').
 *
 * El hook trae todos los registros del período (paginación alta) y suma
 * en cliente. Para volumen alto conviene migrar a endpoints dedicados de
 * agregación — ver §10 de la tabla de optimización pendientes.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { Dayjs } from "dayjs";
import { finanzasEmpresaApi } from "../../services/finanzasEmpresa.api";
import { FiltroFecha } from "./filtroGlobalFecha";
import { agregarFlujos } from "../../utils/financialFlow";
import { flujoAseoAF } from "../../utils/aseoFinance";
import { ahoraChile } from "../../utils/fechaChile";

// ─── Tipos ──────────────────────────────────────────────────────────────────

export interface ResumenPorCategoria {
  /** Ingresos a caja de Aloja Fácil Empresa. */
  ingreso: number;
  /** Egresos desde caja de Aloja Fácil Empresa. */
  egreso: number;
}

export interface ResumenTotales {
  arriendos: ResumenPorCategoria;
  aseos: ResumenPorCategoria;
  lavanderiaExternos: ResumenPorCategoria;
  lavanderiaAF: ResumenPorCategoria;
  sueldos: ResumenPorCategoria;
  gastos: ResumenPorCategoria;
  productos: ResumenPorCategoria;
}

export interface ResumenPorPeriodo {
  /** Etiqueta legible: "Enero 2026" o "2026-01". */
  label: string;
  /** Inicio del período (inclusivo). */
  from: Dayjs;
  /** Fin del período (inclusivo). */
  to: Dayjs;
  totales: ResumenTotales;
  ingresoTotal: number;
  egresoTotal: number;
  utilidad: number;
}

export interface ResumenData {
  loading: boolean;
  error: string | null;
  /** Totales consolidados del período principal seleccionado. */
  totales: ResumenTotales;
  ingresoTotal: number;
  egresoTotal: number;
  utilidad: number;
  /** Serie mensual (últimos 12) cuando filtroFecha.modo === 'mes'. Vacío en rango. */
  porMes: ResumenPorPeriodo[];
  /** Utilidad acumulada del año en curso (sólo en modo mes). */
  utilidadAcumuladaAnio: number;
  /** Refresca manualmente. */
  refetch: () => void;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Traduce FiltroFecha a ventana [from, to] en días. */
function buildRange(ff: FiltroFecha): { from: Dayjs; to: Dayjs } {
  if (ff.modo === "rango" && ff.rango) {
    return {
      from: ff.rango[0].startOf("day"),
      to: ff.rango[1].endOf("day"),
    };
  }
  const m = ff.mes ?? ahoraChile();
  return { from: m.startOf("month"), to: m.endOf("month") };
}

export const zeroTotales = (): ResumenTotales => ({
  arriendos: { ingreso: 0, egreso: 0 },
  aseos: { ingreso: 0, egreso: 0 },
  lavanderiaExternos: { ingreso: 0, egreso: 0 },
  lavanderiaAF: { ingreso: 0, egreso: 0 },
  sueldos: { ingreso: 0, egreso: 0 },
  gastos: { ingreso: 0, egreso: 0 },
  productos: { ingreso: 0, egreso: 0 },
});

const sumField = (rows: any[], field: string): number =>
  rows.reduce((acc, r) => acc + Number(r?.[field] ?? 0), 0);

/** Body común para todos los /search con ventana de fechas. */
function buildSearchBody(from: Dayjs, to: Dayjs) {
  return {
    filters: { fields: {} },
    page: 1,
    // Máximo aceptado por el controller backend (ver finanzasEmpresa.controller.js).
    page_size: 200,
    globalDateRange: [from.format("YYYY-MM-DD"), to.format("YYYY-MM-DD")] as [
      string,
      string,
    ],
  };
}

/**
 * Obtiene TODAS las filas del período iterando páginas de 200.
 * Frena a 10 páginas (2000 filas) como safeguard — con volúmenes mayores
 * corresponde migrar a un endpoint agregado.
 */
async function fetchAllPages(
  searchFn: (body: any) => Promise<any | null>,
  baseBody: any,
): Promise<any[]> {
  const MAX_PAGES = 10;
  const pageSize = baseBody.page_size ?? 200;
  let all: any[] = [];
  for (let page = 1; page <= MAX_PAGES; page++) {
    const res = await searchFn({ ...baseBody, page });
    if (!res?.data) break;
    all = all.concat(res.data);
    const total = Number(res.total ?? all.length);
    if (all.length >= total) break;
    if (res.data.length < pageSize) break;
  }
  return all;
}

/**
 * Agrega los 7 dominios al formato ResumenTotales.
 *
 * Exportada (round 2026-06-17) para reutilizarla desde los gráficos de
 * `/estadisticas-admin`: el panel admin debe ver las MISMAS cifras que las
 * tablas de `/finanzas-empresa` (en vivo, esté o no cerrado el mes), no el
 * cache mensual del cron. Llamándola una vez por mes del rango se obtiene la
 * serie temporal con paridad garantizada. Ver docs/modulo-estadisticas.md §10.
 */
export async function fetchTotalesPeriodo(
  from: Dayjs,
  to: Dayjs,
): Promise<ResumenTotales> {
  const body = buildSearchBody(from, to);

  const [
    arriendosRows,
    sueldosRows,
    gastosRows,
    aseosRows,
    productosRes,
  ] = await Promise.all([
    fetchAllPages(finanzasEmpresaApi.searchArriendos, body),
    fetchAllPages(finanzasEmpresaApi.searchSueldos, body),
    fetchAllPages(finanzasEmpresaApi.searchGastos, body),
    // Round 2026-05-11 iter 26: ya no se cargan lavados acá. Las
    // lavanderías son sub-entidades con contabilidad propia (en
    // `/lavanderia`); no afectan los totales de AF Empresa porque el
    // subitem lavandería del booking ya entró como ingreso vía
    // `limpieza_clp`, y los costos reales (cuando admin paga al externo
    // o registra gasto a la lavandería interna) ya entran como `gastos`
    // categoría manual con `pagado_a=lavandería`. Ver flujo-financiero.md §7.
    fetchAllPages(finanzasEmpresaApi.searchAseos, body),
    // Productos devuelve resumen agregado listo — no necesitamos paginar
    // para sumar, basta una llamada.
    finanzasEmpresaApi.searchProductos(body),
  ]);

  const totales = zeroTotales();

  // Arriendos: ingreso AF ya consolidado por booking (doc §4).
  totales.arriendos.ingreso = sumField(arriendosRows, "ingresoafclp");

  // Round 2026-05-06 (Fase 2): aplicamos el helper canónico
  // `agregarFlujos` para que los movimientos con `rolAF=intermediario`
  // sumen TANTO ingreso como egreso (neto 0). Antes se sumaba `monto`
  // plano como egreso, lo cual:
  //  - Ignoraba `intermediario` (no aparecía como ingreso, hacía bajar
  //    falsamente la utilidad).
  //  - Sumaba `recibe` como egreso (signo invertido).
  // Ver docs/flujo-financiero.md §10.7.

  // Aseos (round 2026-06-18 — fix P1/A1): el flujo del aseo se determina por
  // `pagado_por.tipo` + los 4 subítems, vía el helper canónico `flujoAseoAF`
  // (docs/flujo-financiero.md §6.5). Antes esto pasaba por `agregarFlujos` con
  // `rolDeAlojaFacil`/`pagadoA`, campos ELIMINADOS del aseo en 2026-05-13:
  // `cleaningToFrontend` ya no los emite → `rolAF=null` → `flujoParaAF`
  // devolvía {0,0} para TODO aseo (incluido tipo 0, que es egreso real),
  // dejando los aseos fuera del Resumen e inflando la utilidad. El monto
  // correcto NO es `sueldoAseador` sino los subítems transporte/materiales
  // (ingreso) o los 4 (egreso). Misma regla que la pestaña Aseos.
  for (const a of aseosRows as any[]) {
    const f = flujoAseoAF(a);
    totales.aseos.ingreso += f.ingreso;
    totales.aseos.egreso += f.egreso;
  }

  // Round 2026-05-11 iter 26: las lavanderías NO contribuyen al resumen
  // de AF Empresa según el modelo formalizado por el owner.
  //
  // RAZÓN:
  //   - El subitem lavandería del booking ya entró entero en el INGRESO
  //     de AF Empresa vía `limpieza_clp` (que es uno de los campos de
  //     `arriendos`). Sumar también los lavados sería doble conteo.
  //   - Para AF interna el lavado autogenerado es `intermediario` (entra
  //     y sale, neto AF = 0). Mostrarlo como ingreso/egreso aparte es
  //     ruido — la cancelación contable vive en el cron `utilidad_empresa`.
  //   - Para externa los lavados auto representan el ESTIMADO; no son
  //     ingreso/egreso real para AF Empresa. Solo importa el COSTO REAL
  //     que admin anota a mano (lavados rol='paga' + gastos op.) — esos
  //     ya entran en `gastos` (categoría manual con pagado_a=lavandería).
  //
  // Las lavanderías mantienen su contabilidad propia en `/lavanderia`:
  //   - AF interna: ingresos − gastos op. = utilidad de la sub-entidad.
  //   - Externa: estimado vs real para auditar la calidad del presupuesto.
  // Ver docs/flujo-financiero.md §7 y docs/modulo-lavanderia.md.
  //
  // Las claves `lavanderiaExternos` y `lavanderiaAF` quedan en el shape
  // por compat (la UI las muestra como nota informativa con utilidad propia
  // sin sumar a totales empresa). Su valor inicial = 0 ya está en zeroTotales().

  // Sueldos: usualmente rolAF=paga, pero respetamos el helper por si
  // algún registro tiene intermediario (ej. AF intermedia un pago de
  // propietario a aseador via la tabla gastos categoría=sueldo).
  const sueldosFlujos = agregarFlujos(
    sueldosRows
      // De-dup G11 (round 2026-06-20): un movimiento del aseador `denegado` es
      // auditoría, NO plata real. La ledger del aseador ya lo excluye de sus
      // totales; el Resumen AF debe hacer lo mismo (antes lo contaba e inflaba
      // el bucket sueldos). Sigue visible en la tabla Sueldos.
      .filter((g: any) => g?.estadoPago !== "denegado")
      .map((g: any) => ({
        monto: Number(g?.monto ?? 0),
        rolAF: g?.rolDeAlojaFacil ?? null,
        pagadoPor: g?.pagadoPor ?? null,
        pagadoA: g?.pagadoA ?? null,
      })),
    "AF",
  );
  totales.sueldos.ingreso = sueldosFlujos.ingreso;
  totales.sueldos.egreso = sueldosFlujos.egreso;

  // Gastos: idem (puede haber rolAF=recibe, paga, intermediario).
  //
  // Inventario (round 2026-07-26): los flujos derivados de inventario ya NO
  // llegan por gastos/search (el backend los excluye — viven en /inventario →
  // Finanzas). El ingreso por inventario (RECUPERACIÓN, cobro al cliente)
  // ahora entra al bucket `productos` desde resumen.totalRecuperaciones de
  // productos/search (ver abajo). El filtro por inventoryTxId de acá abajo
  // queda como cinturón-y-tirantes por si llegara una fila legacy (backend
  // viejo con frontend nuevo, respuesta cacheada): se excluye CUALQUIER fila
  // con inventoryTxId, sin mirar el rol — una recuperación (rol=recibe) que
  // se colara sumaría doble, acá y en productos.ingreso.
  //
  // De-dup "otros cargos" (round 2026-06-19, fix G2): los Expense con
  // `reservaId != null` son el ESPEJO de los "otros cargos" de un arriendo
  // (los crea syncAdicionalesToGastos). Su impacto AF YA está en el bucket
  // `arriendos` vía `ingresoafclp` (doc §4.2 suma `otrosCargosNeto`: recibe −
  // paga). Contarlos también acá los duplicaba (recibe sumaba 2×, paga restaba
  // 2×). Se EXCLUYEN del Resumen; siguen visibles/editables en /flujos y en la
  // pestaña Gastos (su fuente real es el booking, no este espejo).
  const gastosFlujos = agregarFlujos(
    gastosRows
      .filter((g: any) => g?.inventoryTxId == null && g?.reservaId == null && g?.lavanderiaId == null && g?.estadoPago !== "denegado")
      .map((g: any) => ({
        monto: Number(g?.monto ?? 0),
        rolAF: g?.rolDeAlojaFacil ?? null,
        pagadoPor: g?.pagadoPor ?? null,
        pagadoA: g?.pagadoA ?? null,
      })),
    "AF",
  );
  totales.gastos.ingreso = gastosFlujos.ingreso;
  totales.gastos.egreso = gastosFlujos.egreso;

  // Productos: las COMPRAS son egreso real. Los MOVIMIENTOS son
  // transferencias internas (bodega → propiedad), no tocan caja.
  // Round 2026-07-26 — el bucket es ahora AUTOCONTENIDO: el ingreso es la
  // RECUPERACIÓN (cobro al cliente, monto editable, Σ del período desde
  // resumen.totalRecuperaciones) porque esos flujos ya no llegan por
  // gastos/search. El reembolso al tercero sigue EXCLUIDO del Resumen (es el
  // mismo costo que totalCompras — doble conteo).
  totales.productos.egreso = Number(productosRes?.resumen?.totalCompras ?? 0);
  totales.productos.ingreso = Number(productosRes?.resumen?.totalRecuperaciones ?? 0);

  return totales;
}

export function sumarTotales(t: ResumenTotales): {
  ingresoTotal: number;
  egresoTotal: number;
  utilidad: number;
} {
  const cats = Object.values(t);
  const ingresoTotal = cats.reduce((a, c) => a + c.ingreso, 0);
  const egresoTotal = cats.reduce((a, c) => a + c.egreso, 0);
  return { ingresoTotal, egresoTotal, utilidad: ingresoTotal - egresoTotal };
}

// ─── Hook ──────────────────────────────────────────────────────────────────

export function useResumenLogic(
  filtroFecha?: FiltroFecha,
  /**
   * Refresco externo (botón "Actualizar" de /finanzas-empresa). Al cambiar,
   * re-dispara ambos efectos de carga igual que el `reloadKey` interno del
   * `refetch`, sin tocar el mes seleccionado.
   */
  refreshKey?: number,
): ResumenData {
  const ff: FiltroFecha = filtroFecha ?? { modo: "mes", mes: ahoraChile() };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totales, setTotales] = useState<ResumenTotales>(zeroTotales);
  const [porMes, setPorMes] = useState<ResumenPorPeriodo[]>([]);
  const [reloadKey, setReloadKey] = useState(0);

  // Clave estable para el useEffect — evita depender del objeto `ff`.
  const filtroKey = useMemo(() => {
    if (ff.modo === "rango" && ff.rango) {
      return `rango:${ff.rango[0].format("YYYY-MM-DD")}:${ff.rango[1].format("YYYY-MM-DD")}`;
    }
    return `${ff.modo}:${(ff.mes ?? ahoraChile()).format("YYYY-MM")}`;
  }, [ff]);

  // Round 2026-04-29 (fix Sentry FCP/LCP poor en /finanzas-empresa):
  // antes este efecto disparaba `fetchTotalesPeriodo` (7 endpoints) para
  // **13 períodos** (principal + 12 meses) con `Promise.all`. Resultado:
  // 84-91 fetches concurrentes en el primer render — el navegador queda
  // asfixiado, los breadcrumbs Sentry mostraban docenas de POST /search
  // duplicados, y FCP/LCP se hundían a >4s en producción.
  //
  // Fix de 3 partes:
  //   1. Cargar SOLO el período principal en este efecto (7 fetches). Los
  //      KPIs y el desglose de la pestaña aparecen rápido — eso es lo
  //      que mide FCP/LCP.
  //   2. Marcar `loading=false` ni bien llega el principal. La UI ya está
  //      pintable mientras la serie se carga atrás.
  //   3. La serie mensual se carga en un efecto separado, con concurrency
  //      limitada (3 a la vez en vez de 12) y diferida a `requestIdleCallback`
  //      para no robar tiempo a la pintada inicial.
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const { from, to } = buildRange(ff);
        const totalesPrincipal = await fetchTotalesPeriodo(from, to);
        if (cancelled) return;
        setTotales(totalesPrincipal);
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.message || "Error al cargar resumen");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroKey, reloadKey, refreshKey]);

  // Efecto separado para la serie mensual de 12 meses. Se dispara DESPUÉS
  // del principal (depende de `filtroKey` igual, pero corre en paralelo).
  // Concurrency 3 evita asfixiar al backend (pool de Postgres ~10-15) y
  // al navegador (limite de conexiones HTTP/2 por host es ~6). El uso
  // de `requestIdleCallback` cuando está disponible posterga el arranque
  // hasta que el navegador termine de pintar lo crítico.
  useEffect(() => {
    if (ff.modo !== "mes" && ff.modo !== "anterior") {
      setPorMes([]);
      return undefined;
    }

    let cancelled = false;
    let idleHandle: number | undefined;

    const start = async () => {
      const baseMes = (ff.mes ?? ahoraChile()).startOf("month");
      const meses: { from: Dayjs; to: Dayjs; label: string }[] = [];
      for (let i = 11; i >= 0; i--) {
        const m = baseMes.subtract(i, "month");
        meses.push({
          from: m.startOf("month"),
          to: m.endOf("month"),
          label: m.format("MMMM YYYY"),
        });
      }

      // Concurrency limit: procesa los 12 meses en grupos de 3.
      // 4 batches × ~7 fetches/batch = ~28 fetches concurrentes pico,
      // muy por debajo del límite del browser. Total tarda 4× lo que
      // tardaba el batch original, pero corre en background después
      // del FCP del usuario.
      const BATCH = 3;
      const serie: ResumenPorPeriodo[] = [];
      for (let i = 0; i < meses.length; i += BATCH) {
        if (cancelled) return;
        const grupo = meses.slice(i, i + BATCH);
        const partial = await Promise.all(
          grupo.map((m) =>
            fetchTotalesPeriodo(m.from, m.to).then((tot) => {
              const { ingresoTotal, egresoTotal, utilidad } = sumarTotales(tot);
              return {
                label: m.label,
                from: m.from,
                to: m.to,
                totales: tot,
                ingresoTotal,
                egresoTotal,
                utilidad,
              } as ResumenPorPeriodo;
            }),
          ),
        );
        if (cancelled) return;
        serie.push(...partial);
        // Actualiza progresivamente — el usuario ve los meses llegando
        // en lugar de un loading screen total.
        setPorMes([...serie]);
      }
    };

    // Si el navegador soporta requestIdleCallback, espera a que la
    // primera pintada del Resumen termine antes de empezar la serie.
    // Fallback: setTimeout(0) — al final del event loop.
    const ric = (window as any).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout?: number }) => number)
      | undefined;
    if (typeof ric === "function") {
      idleHandle = ric(() => void start(), { timeout: 1500 });
    } else {
      idleHandle = window.setTimeout(() => void start(), 0);
    }

    return () => {
      cancelled = true;
      if (idleHandle != null) {
        const cic = (window as any).cancelIdleCallback as
          | ((id: number) => void)
          | undefined;
        if (typeof cic === "function") cic(idleHandle);
        else window.clearTimeout(idleHandle);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroKey, reloadKey, refreshKey]);

  const { ingresoTotal, egresoTotal, utilidad } = useMemo(
    () => sumarTotales(totales),
    [totales],
  );

  const utilidadAcumuladaAnio = useMemo(() => {
    if (porMes.length === 0) return 0;
    const baseMes = (ff.mes ?? ahoraChile()).startOf("month");
    const anio = baseMes.year();
    return porMes
      .filter((m) => m.from.year() === anio)
      .reduce((acc, m) => acc + m.utilidad, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [porMes, filtroKey]);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  return {
    loading,
    error,
    totales,
    ingresoTotal,
    egresoTotal,
    utilidad,
    porMes,
    utilidadAcumuladaAnio,
    refetch,
  };
}
